import clsx from 'clsx';
import React, { FC, useMemo } from 'react';
import styles from './text.module.scss';

// https://www.aleksandrhovhannisyan.com/blog/dynamic-tag-name-props-in-react/
interface IProps extends React.HTMLAttributes<HTMLOrSVGElement> {
    tagName?: keyof JSX.IntrinsicElements;
    textStyle?: 'body' | 'bodySmall' | 'bodyLarge' | 'bodyExtraSmall' | 'monoSmall' | 'monoMedium' | 'monoLarge';
    fontWeight?: 'light' | 'regular' | 'semiBold' | 'bold';
    nowrap?: boolean;
    secondary?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    color?: 'bodyColor' | 'primaryColor' | 'errorColor' | 'lightColor';
    overflowEllipsis?: boolean;
    highlightText?: string;
    text?: string;
}

const Text: FC<IProps> = ({
    textStyle = 'body',
    tagName = 'div',
    color,
    textAlign,
    fontWeight,
    className,
    children,
    nowrap,
    highlightText,
    text,
    secondary,
    overflowEllipsis,
    ...rest
}) => {
    const Tag = tagName as keyof JSX.IntrinsicElements;

    const renderedText = useMemo(() => {
        if (!highlightText?.trim()) {
            return <>{text}</>;
        }

        const regex = new RegExp(`(${highlightText})`, 'gi');
        const parts = text?.split(regex);

        return <>{parts?.filter(String).map((part, i) => (regex.test(part) ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>))}</>;
    }, [highlightText, text]);

    return (
        <Tag
            className={clsx(
                className,
                styles.root,
                styles[textAlign as string],
                styles[textStyle],
                styles[fontWeight as string],
                styles[color as string],
                {
                    [styles.nowrap]: nowrap,
                    [styles.secondary]: secondary,
                    [styles.overflowEllipsis]: overflowEllipsis,
                },
            )}
            {...rest}
        >
            {text ? renderedText : children}
        </Tag>
    );
};

export default Text;
